import clsx from "clsx";

import { DesktopAppointmentSummary } from "~/components/Appointment/DesktopAppointmentSummary";
import { CheckMarkIconSolid } from "~/components/Icons/CheckmarkIcon";
import { useStepLinkState } from "~/hooks/useStepLinkState";
import { useStepTitle } from "~/hooks/useStepTitle";
import { GoBackButton } from "~/modules/go-back-api";
import { Step } from "~/modules/StateManager";
import { useStateManager } from "~/modules/StateManager.hooks";
import { SummaryProps } from "~/routes/$enterprise+/plugin+/$sessionId.schedule";
import type { StepConfig } from "~/utils/getFlow";

export function DesktopSummaryContainer({
  hideLockedSteps = false,
  info,
}: Omit<SummaryProps, "children">) {
  const stateManager = useStateManager();
  const { location, timetable } = stateManager.state;
  const { employee, meetingType, office, subject } = info;

  const visibleSteps = stateManager.visibleSteps.filter((step) =>
    hideLockedSteps ? !step.locked : true,
  );

  const { step: employeeStep } = stateManager.getStepConfig("employee");
  const showEmployeePlaceholder = employeeStep?.visible && !!employee;

  return (
    <div className="grid content-start gap-md" data-component="DesktopSummaryContainer">
      <ul className="flow-root">
        {visibleSteps.map((step, index) => (
          <li key={step.id}>
            <div className="relative pb-base" data-component="StepWrapper">
              {index !== visibleSteps.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="absolute left-[9px] top-[16px] h-full w-[2px] bg-gray-200"
                  data-component="StepConnector"
                />
              ) : null}
              <StepTitle step={step} />
            </div>
          </li>
        ))}
      </ul>
      <DesktopAppointmentSummary
        employee={employee}
        location={{
          geolocation: location?.geolocation ?? null,
          address: location?.formattedAddress ?? null,
        }}
        meetingType={meetingType}
        office={office}
        possibility={
          timetable.start && timetable.end ? { start: timetable.start, end: timetable.end } : null
        }
        showEmployeePlaceholder={showEmployeePlaceholder}
        subject={subject}
      />
    </div>
  );
}
function StepTitle({ step }: { step: Step }) {
  const { isActive, isEditable } = useStepLinkState({ step });

  const title = useStepTitle(step);

  const isInactive = !(step.hasValue || isActive);

  return (
    <StepLink isEditable={isEditable} stepId={step.id}>
      <div className="relative flex items-center gap-sm">
        <div className="flex">
          {step.hasValue ? (
            <div className="m-px rounded-full bg-secondary" data-component="StepIndicatorChecked">
              <CheckMarkIconSolid className="m-[2px]  text-white" height={14} width={14} />
            </div>
          ) : (
            <div
              className={clsx(
                "m-[3px] h-[14px] w-[14px] rounded-full",
                isActive ? "bg-secondary ring-4 ring-secondary/20" : "bg-gray-200",
              )}
              data-active={isActive}
              data-component="StepIndicator"
            />
          )}
        </div>
        <div
          className={clsx(
            "text-sm",
            isInactive && "text-gray-500",
            isActive ? "font-semibold" : "font-medium",
          )}
          data-active={isActive}
          data-checked={step.hasValue}
          data-component="StepTitle"
        >
          {title}
        </div>
      </div>
    </StepLink>
  );
}

interface StepLinkProps extends React.PropsWithChildren {
  isEditable?: boolean;
  stepId: StepConfig["id"];
}

function StepLink({ children, isEditable, stepId }: StepLinkProps) {
  if (!isEditable) {
    return <>{children}</>;
  }

  return (
    <GoBackButton
      className={clsx("flex", "border-none", "text-secondary", "hover:underline")}
      stepId={stepId}
    >
      {children}
    </GoBackButton>
  );
}
